import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { taskAPI, employeeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../components/EmployeeManagement.css';

const TaskManagement = () => {
  const { currentUser, hasPermission } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [allTasks, setAllTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isPrivileged = hasPermission('employee_view');

  // form state
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ title: '', assignedTo: '', startDate: today, dueDate: '', priority: 'medium', description: '', category: 'other' });

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const res = await taskAPI.getAll();
      setAllTasks(res?.data || res || []);
    } catch (e) {
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
    if (isPrivileged) {
      employeeAPI.getAll().then(r => setEmployees(r?.data || r || [])).catch(() => {});
    }
  }, []);

  const tasks = {
    pending:    allTasks.filter(t => t.status === 'pending'),
    inProgress: allTasks.filter(t => t.status === 'in-progress'),
    completed:  allTasks.filter(t => t.status === 'completed'),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: form.title,
        description: form.description,
        assignedTo: form.assignedTo ? [form.assignedTo] : [],
        startDate: form.startDate,
        dueDate: form.dueDate,
        priority: form.priority,
        category: form.category,
      };
      await taskAPI.create(payload);
      setShowModal(false);
      setForm({ title: '', assignedTo: '', startDate: today, dueDate: '', priority: 'medium', description: '', category: 'other' });
      fetchTasks();
    } catch (err) {
      setError(err?.message || err?.error || 'Failed to create task');
    }
  };

  const priorityClass = { high: 'priority high', medium: 'priority medium', low: 'priority low', urgent: 'priority high' };

  const handleStatusUpdate = async (taskId, newStatus) => {
    try {
      await taskAPI.update(taskId, { status: newStatus });
      fetchTasks();
    } catch (err) {
      setError('Failed to update task status');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await taskAPI.delete(taskId);
      fetchTasks();
    } catch (err) {
      setError('Failed to delete task');
    }
  };

  const TaskCard = ({ task }) => {
    const assignees = Array.isArray(task.assignedTo) ? task.assignedTo : (task.assignedTo ? [task.assignedTo] : []);
    const statusOrder = ['pending', 'in-progress', 'on-hold', 'completed'];
    return (
      <div className="task-card" style={{ borderLeftColor: task.status === 'completed' ? '#10b981' : task.status === 'in-progress' ? '#f59e0b' : '#667eea' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <h4 style={{ margin: 0, flex: 1 }}>{task.title}</h4>
          {isPrivileged && (
            <button
              onClick={() => handleDelete(task._id)}
              title="Delete Task"
              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 0 0 8px', fontSize: 14 }}
            >
              <i className="fas fa-trash"></i>
            </button>
          )}
        </div>
        {isPrivileged && assignees.length > 0 && (
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>
            <i className="fas fa-user" style={{ marginRight: 4 }}></i>
            {assignees.map(e => e?.name || e).join(', ')}
          </p>
        )}
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
          {task.dueDate ? `Due: ${new Date(task.dueDate).toLocaleDateString()}` : ''}
        </p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
          <span className={priorityClass[task.priority] || 'priority medium'} style={{ textTransform: 'capitalize' }}>
            {task.priority || 'Medium'} Priority
          </span>
          <select
            value={task.status}
            onChange={e => handleStatusUpdate(task._id, e.target.value)}
            style={{ fontSize: 12, padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db', cursor: 'pointer', background: '#f9fafb' }}
          >
            {statusOrder.map(s => (
              <option key={s} value={s} style={{ textTransform: 'capitalize' }}>
                {s.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  return (
    <div className="task-management">
      <div className="module-header">
        <h2>Task Management</h2>
        {isPrivileged && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="fas fa-plus"></i> Assign Task
          </button>
        )}
      </div>

      {error && <div className="error-banner" style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 32, marginBottom: 12 }}></i>
          <p>Loading tasks...</p>
        </div>
      ) : (
        <div className="task-board">
          {[['pending', 'Pending', 'fa-clock'], ['inProgress', 'In Progress', 'fa-spinner'], ['completed', 'Completed', 'fa-check-circle']].map(([key, label, icon]) => (
            <div key={key} className="task-column">
              <h3><i className={`fas ${icon}`}></i> {label}</h3>
              {tasks[key].length === 0
                ? <p style={{ color: '#9ca3af', fontSize: 13 }}>No tasks</p>
                : tasks[key].map(task => <TaskCard key={task._id} task={task} />)
              }
            </div>
          ))}
        </div>
      )}

      {isPrivileged && (
        <Modal show={showModal} onClose={() => setShowModal(false)} title="Assign New Task">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Task Title</label>
              <input type="text" className="form-input" placeholder="Task title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Assign To</label>
              <select className="form-input" value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })} required>
                <option value="">Select Employee</option>
                {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name} ({emp.employeeId})</option>)}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input type="date" className="form-input" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Due Date</label>
                <input type="date" className="form-input" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Priority</label>
                <select className="form-input" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="form-group">
                <label>Category</label>
                <select className="form-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  <option value="development">Development</option>
                  <option value="design">Design</option>
                  <option value="marketing">Marketing</option>
                  <option value="sales">Sales</option>
                  <option value="support">Support</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea className="form-input" rows="3" placeholder="Task description..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required></textarea>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Assign Task</button>
            </div>
          </form>
        </Modal>
      )}

      <style jsx>{`
        .task-board { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; }
        .task-column { background: white; padding: 20px; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
        .task-column h3 { display: flex; align-items: center; gap: 10px; margin-bottom: 20px; font-size: 18px; color: var(--text-primary); }
        .task-card { background: var(--light-color, #f9fafb); padding: 15px; border-radius: 8px; margin-bottom: 15px; border-left: 4px solid #667eea; transition: transform 0.3s ease, box-shadow 0.3s ease; cursor: pointer; }
        .task-card:hover { transform: translateX(5px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
        .task-card h4 { margin-bottom: 10px; color: var(--text-primary); }
        .task-card p { font-size: 13px; color: var(--text-secondary, #6b7280); margin-bottom: 5px; }
        .priority { display: inline-block; padding: 4px 10px; border-radius: 15px; font-size: 11px; font-weight: 600; margin-top: 10px; }
        .priority.high { background: #fee2e2; color: #991b1b; }
        .priority.medium { background: #fef3c7; color: #92400e; }
        .priority.low { background: #dbeafe; color: #1e40af; }
      `}</style>
    </div>
  );
};

export default TaskManagement;
